import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, ArrowUpDown, MapPin, DollarSign, Calendar, Flame, Thermometer, Snowflake, Send, ChevronLeft, ChevronRight, LogOut, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const FRESHNESS_CONFIG = {
  hot: { icon: Flame, color: 'text-red-500', bg: 'bg-red-50', label: 'Hot' },
  warm: { icon: Thermometer, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Warm' },
  cold: { icon: Snowflake, color: 'text-blue-400', bg: 'bg-blue-50', label: 'Cold' }
};

export default function BuyerDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ make: '', year_min: '', year_max: '', condition: '', price_min: '', price_max: '', zip_code: '', sort_by: 'newest' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 12, skip: (page - 1) * 12 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get('/api/junk-leads/enhanced', { params });
      setLeads(data.leads);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Buyer access required');
      }
    } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleOffer = async () => {
    if (!offerAmount || Number(offerAmount) <= 0) { toast.error('Enter a valid offer amount'); return; }
    setSubmitting(true);
    try {
      await api.post(`/api/junk-leads/${selectedLead.id}/offer`, {
        amount: Number(offerAmount), message: offerMessage
      });
      toast.success('Offer submitted!');
      setSelectedLead(null);
      setOfferAmount('');
      setOfferMessage('');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit offer');
    } finally { setSubmitting(false); }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-lg">Junk Car Marketplace</h1>
              <p className="text-xs text-gray-500">{total} leads available</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <span className="text-sm text-gray-600 hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={logout} className="rounded-full" data-testid="buyer-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Filters Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Input value={filters.make} onChange={e => updateFilter('make', e.target.value)} placeholder="Search make..." className="w-40 rounded-xl" data-testid="filter-make" />
            <Input value={filters.zip_code} onChange={e => updateFilter('zip_code', e.target.value)} placeholder="ZIP code" className="w-28 rounded-xl" data-testid="filter-zip" />
            <Select value={filters.condition || 'all'} onValueChange={v => updateFilter('condition', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-32 rounded-xl" data-testid="filter-condition">
                <SelectValue placeholder="Condition" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="excellent">Excellent</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="poor">Poor</SelectItem>
                <SelectItem value="salvage">Salvage</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.sort_by} onValueChange={v => updateFilter('sort_by', v)}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="filter-sort">
                <ArrowUpDown className="w-3.5 h-3.5 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="year_new">Year: Newest</SelectItem>
                <SelectItem value="fewest_offers">Fewest Offers</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-xl" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-advanced-filters">
              <Filter className="w-4 h-4 mr-1.5" /> More
            </Button>
          </div>
          {showFilters && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 flex-wrap">
              <Input value={filters.year_min} onChange={e => updateFilter('year_min', e.target.value)} placeholder="Year from" className="w-28 rounded-xl" type="number" data-testid="filter-year-min" />
              <Input value={filters.year_max} onChange={e => updateFilter('year_max', e.target.value)} placeholder="Year to" className="w-28 rounded-xl" type="number" data-testid="filter-year-max" />
              <Input value={filters.price_min} onChange={e => updateFilter('price_min', e.target.value)} placeholder="Min price" className="w-28 rounded-xl" type="number" data-testid="filter-price-min" />
              <Input value={filters.price_max} onChange={e => updateFilter('price_max', e.target.value)} placeholder="Max price" className="w-28 rounded-xl" type="number" data-testid="filter-price-max" />
              <Button variant="ghost" size="sm" onClick={() => { setFilters({ make: '', year_min: '', year_max: '', condition: '', price_min: '', price_max: '', zip_code: '', sort_by: 'newest' }); setPage(1); }} className="text-red-500" data-testid="clear-filters">
                Clear All
              </Button>
            </div>
          )}
        </div>

        {/* Leads Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <div key={i} className="bg-white rounded-2xl h-56 animate-pulse" />)}
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium text-gray-500">No leads match your criteria</p>
            <p className="text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {leads.map(lead => {
              const freshness = FRESHNESS_CONFIG[lead.freshness] || FRESHNESS_CONFIG.cold;
              const FIcon = freshness.icon;
              return (
                <div key={lead.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-lg transition-all group" data-testid={`lead-card-${lead.id}`}>
                  {/* Score & Freshness Bar */}
                  <div className="flex items-center justify-between px-4 pt-3">
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${freshness.bg} ${freshness.color}`}>
                        <FIcon className="w-3 h-3" /> {freshness.label}
                      </div>
                      {lead.offer_count === 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">No offers yet</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1" title="Lead Score">
                      <div className="w-8 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${lead.lead_score >= 80 ? 'bg-emerald-500' : lead.lead_score >= 50 ? 'bg-amber-400' : 'bg-gray-300'}`} style={{ width: `${lead.lead_score}%` }} />
                      </div>
                      <span className="text-xs text-gray-400">{lead.lead_score}</span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-lg mb-1">{lead.year} {lead.make} {lead.model}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">{lead.condition}</span>
                      {lead.vin && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-lg font-mono">{lead.vin.slice(0,8)}...</span>}
                    </div>
                    <div className="space-y-1.5 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> {lead.location} ({lead.zip_code})
                      </div>
                      {lead.asking_price && (
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="w-3.5 h-3.5" /> Asking: <span className="font-semibold text-gray-900">${lead.asking_price.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> {new Date(lead.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    {lead.description && (
                      <p className="text-xs text-gray-400 mb-3 line-clamp-2">{lead.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{lead.offer_count} offer{lead.offer_count !== 1 ? 's' : ''}</span>
                      <Button
                        size="sm"
                        className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90"
                        onClick={() => setSelectedLead(lead)}
                        data-testid={`make-offer-${lead.id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5" /> Make Offer
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 py-4" data-testid="lead-pagination">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-xl">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-gray-500">Page {page} of {pages}</span>
            <Button variant="outline" size="sm" disabled={page === pages} onClick={() => setPage(p => p + 1)} className="rounded-xl">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>

      {/* Offer Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={v => { if (!v) setSelectedLead(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Make Offer - {selectedLead?.year} {selectedLead?.make} {selectedLead?.model}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {selectedLead?.asking_price && (
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
                Asking price: <span className="font-bold text-gray-900">${selectedLead.asking_price.toLocaleString()}</span>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Your Offer ($) *</label>
              <Input value={offerAmount} onChange={e => setOfferAmount(e.target.value)} type="number" className="mt-1 rounded-xl" placeholder="e.g. 2500" data-testid="offer-amount-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Message (optional)</label>
              <Textarea value={offerMessage} onChange={e => setOfferMessage(e.target.value)} className="mt-1 rounded-xl" placeholder="I'm interested in this vehicle..." rows={3} data-testid="offer-message-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setSelectedLead(null)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleOffer} disabled={submitting} className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90" data-testid="submit-offer-btn">
                {submitting ? 'Submitting...' : 'Submit Offer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
